import React, { useState, useEffect } from "react";
import { ArrowLeft, DownloadIcon, PenBoxIcon } from "lucide-react";
import { formatCurrency } from "../../tools/tool";
import { adminServices } from "../../services/AdminServices";
import { toast } from "react-toastify";
import CreateDeliveryNoteModal from "../../components/CreateDeliveryNoteModal";
import EditDeliveryNoteModal from "../../components/EditDeliveryNoteModal";
import CreateSalesOrderModal from "../../components/CreateSalesOrderModal";
import EditSalesOrderModal from "../../components/EditSalesOrderModal";
import EditPOModal from "../../components/EditPOModal";
import { jwtDecode } from "jwt-decode";

const ProcessOrderView = ({ orderId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);
  const [merchantWallet, setMerchantWallet] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [remarks, setRemarks] = useState("");
  const [abstractApprovalRemarks, setAbstractApprovalRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const [rfq, setRfq] = useState(null);
  const [rfqLoading, setRfqLoading] = useState(false);
  const [po, setPo] = useState(null);
  const [poList, setPoList] = useState([]);
  const [poLoading, setPoLoading] = useState(false);
  const [editingPO, setEditingPO] = useState(false);
  const [showDeliveryNoteModal, setShowDeliveryNoteModal] = useState(false);
  const [showEditDeliveryNoteModal, setShowEditDeliveryNoteModal] = useState(false);
  const [deliveryNotePO, setDeliveryNotePO] = useState(null);
  const [editDeliveryNoteId, setEditDeliveryNoteId] = useState(null);
  const [showSalesOrderModal, setShowSalesOrderModal] = useState(false);
  const [showEditSalesOrderModal, setShowEditSalesOrderModal] = useState(false);
  const [salesOrderDN, setSalesOrderDN] = useState(null);
  const [editSalesOrderId, setEditSalesOrderId] = useState(null);
  const [poDetails, setPoDetails] = useState(null);
  const [poDetailsLoading, setPoDetailsLoading] = useState(false);
  const [enrichedSuppliers, setEnrichedSuppliers] = useState({});
  const [supplier, setSupplier] = useState(null);
  const [poNo, setPoNo] = useState({});
  const [userInfo, setUserInfo] = useState(null);
  const [remarksForMerchant, setRemarksForMerchant] = useState("");
  const [savingRemarksForMerchant, setSavingRemarksForMerchant] = useState(false);
  const [selectedPoIdForDelivery, setSelectedPoIdForDelivery] = useState("");
  const [deliveryEtaInput, setDeliveryEtaInput] = useState("");
  const [savingDeliveryEta, setSavingDeliveryEta] = useState(false);
  const [deliveryNotesList, setDeliveryNotesList] = useState([]);
  const [salesOrdersList, setSalesOrdersList] = useState([]);

  const getEncoderId = () => {
    try {
      const token = localStorage.getItem('appToken');
      if (!token) return 0;
      const decoded = jwtDecode(token);
      const userId = decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'];
      return Number(userId) || 0;
    } catch {
      return 0;
    }
  };

  const buildDeliveryNotePayload = (payload, isDraft) => ({
    p_purchase_order_id: Number(payload.purchaseOrderId) || 0,
    p_ipx_order_id: Number(orderDetails?.order?.orderId) || 0,
    p_encoder_id: getEncoderId(),
    p_inspected_by: payload.processedBy || "",
    p_noted_by: payload.processedBy || "",
    p_received_by: "",
    p_remarks: payload.remarks || "",
    p_is_draft: isDraft ? 1 : 0,
    p_items: JSON.stringify(payload.items || []),
  });

  const buildUpdateDeliveryNotePayload = (payload, isDraft) => ({
    p_delivery_note_id: Number(editDeliveryNoteId) || 0,
    p_inspected_by: payload.processedBy || "",
    p_noted_by: payload.processedBy || "",
    p_received_by: "",
    p_remarks: payload.remarks || "",
    p_is_draft: isDraft ? 1 : 0,
    p_items: JSON.stringify(payload.items || []),
  });

  const buildSalesOrderPayload = (payload, isDraft) => {
    const total = (payload.items || []).reduce(
      (sum, item) => sum + Number(item.quantity ?? item.qty ?? 0) * Number(item.unitPrice ?? 0),
      0
    );
    const walletAmount = merchantWallet?.wallet_amount ?? merchantWallet?.walletAmount ?? 0;
    const balanceAfter = Number(walletAmount || 0) - total;
    const result = {
      p_delivery_note_id: Number(payload.deliveryNoteId) || 0,
      p_processed_by_id: getEncoderId(),
      p_noted_by: payload.processedBy || "",
      p_certified_correct: payload.certifiedCorrect || "",
      p_received_payment: payload.receivedPayment || "",
      p_remarks: payload.remarks || "",
      p_is_draft: isDraft ? 1 : 0,
      p_merchant_reserved_funds: total,
      p_balance_after: Number.isFinite(balanceAfter) ? balanceAfter : 0,
      p_items: JSON.stringify((payload.items || []).map((item) => ({
        productId: item.productId ?? item.product_id,
        quantity: item.quantity ?? item.qty ?? 0,
        unitPrice: item.unitPrice ?? 0,
      }))),
    };
    // eslint-disable-next-line no-console
    console.log("[SO] buildSalesOrderPayload:", result);
    return result;
  };

  const buildUpdateSalesOrderPayload = (payload, isDraft) => ({
    p_sales_order_id: Number(editSalesOrderId) || 0,
    p_processed_by_id: getEncoderId(),
    p_noted_by: payload.processedBy || "",
    p_certified_correct: payload.certifiedCorrect || "",
    p_received_payment: payload.receivedPayment || "",
    p_remarks: payload.remarks || "",
    p_is_draft: isDraft ? 1 : 0,
    p_merchant_reserved_funds: payload.merchantReservedFunds ?? 0,
    p_balance_after: payload.balanceAfter ?? 0,
    p_items: JSON.stringify(payload.items || []),
  });

  useEffect(() => {
    if(supplier) {
      getUserInfo();
    }
  },[supplier])

  const getUserInfo = async ()=>{
     await adminServices.storeProcedure2('getUserInfo',{"userId": poNo.purchaserId}).then((res)=>{
        setUserInfo(res.data[0]);
     }).catch((err)=>{
        console.error(err)
     })
  }

  useEffect(() => {
    if(userInfo) {
      setPoDetails({
        purchaseOrderId: poNo.purchase_order_id || 0,
        poNo: "PO-" + poNo.purchase_order_id || '',
        prNo: orderDetails?.managerApprovals?.[0]?.prNo || '',
        supplierName: supplier?.supplier_name || '',
        dateIssued: new Date().toISOString().split('T')[0],
        address: supplier?.address || '',
        contactPerson: supplier?.contact_name || '',
        expectedDeliveryDate:poNo.delivery_eta || '',
        paymentTerms: poNo.payment_terms || '',
        items: orderDetails?.purchaseOrders?.[0]?.items || [],
        total: 0,
        requestedBy: userInfo.accountName || '',
        phone: userInfo.mobileNumber || '',
        email: userInfo.emailAddress || '',
        remarks: poNo.po_remarks
      });
      setPoDetailsLoading(false);
    }
  },[userInfo])

  useEffect(() => {
    if (orderId) {
      fetchOrderDetails();
    }
  }, [orderId]);

  useEffect(() => {
    if (orderId) fetchRFQ();
  }, [orderId]);

  useEffect(() => {
    if (orderId) fetchPO();
  }, [orderId]);

  useEffect(() => {
    if (orderId) fetchDeliveryNotes();
  }, [orderId]);

  useEffect(() => {
    if (orderId) fetchSalesOrders();
  }, [orderId]);

  const fetchDeliveryNotes = async () => {
    try {
      const response = await adminServices.storeProcedure2('getDeliveryNotesByOrderId', { orderId: Number(orderId) });
      setDeliveryNotesList(response?.data || []);
    } catch (error) {
      console.error("Failed to fetch delivery notes:", error);
      setDeliveryNotesList([]);
    }
  };

  const fetchSalesOrders = async () => {
    try {
      const response = await adminServices.storeProcedure2('getSalesOrdersByOrderId', { orderId: Number(orderId) });
      const data = response?.data?.data ?? response?.data?.result ?? response?.data?.results ?? response?.data ?? [];
      setSalesOrdersList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch sales orders:", error);
      setSalesOrdersList([]);
    }
  };

  const fetchPO = async () => {
    try {
      setPoLoading(true);
      const response = await adminServices.getPoAttachmentByOrderId(orderId);
      const data = response?.data;

      setPoList(data || []);
     
    } catch (error) {
      console.error("Failed to fetch PO:", error);
      setPoList([]);
      setPo(null);
    } finally {
      setPoLoading(false);
    }
  };

  useEffect(() => {
    // no-op: poList/poDetails changes are handled by rendering
  }, [poList,poDetails]);

  const fetchRFQ = async () => {
    try {
      setRfqLoading(true);
      const response = await adminServices.getRFQByOrderId(orderId);
      const data = response?.data?.data ?? response?.data ?? response?.data;
      if (Array.isArray(data) && data.length > 0) {
        setRfq(data[0]);
      } else if (Array.isArray(response?.data) && response.data.length > 0) {
        setRfq(response.data[0]);
      } else {
        setRfq(null);
      }
    } catch (error) {
      console.error("Failed to fetch RFQ:", error);
      setRfq(null);
    } finally {
      setRfqLoading(false);
    }
  };

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await adminServices.getOrderDetails(orderId);
      setOrderDetails(response.data);

      // Fetch merchant wallet
      if (response.data?.order?.merchantId) {
        await fetchMerchantWallet(response.data.order.merchantId);
      }
    } catch (error) {

      if(error.status == 401){
        window.location.href = "/";
        
      }
      console.error("Failed to fetch order details:", error);
      toast.error("Failed to fetch order details");


    } finally {
      setLoading(false);
    }
  };

  const fetchMerchantWallet = async (merchantId) => {
    try {
      const response = await adminServices.getMerchantWallet(merchantId);
      if (response.data && response.data.length > 0) {
        setMerchantWallet(response.data[0]);

        
      }
    } catch (error) {
      console.error("Failed to fetch merchant wallet:", error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    });
  };

  const handleSave = async () => {
    try {
      const quotationRequestId = orderDetails?.managerApprovals?.[0]?.purchaseQuotationId;
      if (!quotationRequestId) {
        toast.error("Quotation request ID not found");
        return;
      }

      setSaving(true);

      const items = [];
      Object.entries(selectedItems).forEach(([itemIndex, supplierIndex]) => {
        const item = supplierData[parseInt(itemIndex)];
        const supplier = suppliers[supplierIndex];
        const supplierItem = item?.suppliers?.[supplierIndex];
        if (!item || !supplier || !supplierItem) return;

        items.push({
          productId: item.productId,
          supplierId: supplier.supplierId,
          uomId: item.uomId,
          quantity: item.qty,
          unitPrice: supplierItem.costPrice,
          subtotal: supplierItem.total,
          remarks: ""
        });
      });

      await adminServices.updateQuotationRequestItems(quotationRequestId, {
        supplierRecommendationRemarks: remarks,
        items
      });

      toast.success("Order saved successfully");
      await fetchOrderDetails();
    } catch (error) {
      console.error("Failed to save order:", error);
      toast.error(error.response?.data?.message || "Failed to save order");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleApprove = async (type) => {
    if (type === "Abstract") {
      const purchaseQuotationId = orderDetails?.managerApprovals?.[0]?.purchaseQuotationId;
      const prNo = orderDetails?.managerApprovals?.[0]?.prNo;
      if (!purchaseQuotationId) {
        toast.error("Quotation request ID not found");
        return;
      }
      try {
        const payload = {
          purchaseQuotationId,
          prNo: prNo || "",
          status: "APPROVED",
          approvalRemarks: abstractApprovalRemarks || ""
        };
        await adminServices.approveQuotationRequest(payload);
        toast.success("Abstract quotation approved");
        await fetchOrderDetails();
        await fetchRFQ();
      } catch (error) {
        console.error("Failed to approve abstract quotation:", error);
        toast.error(error.response?.data?.message || "Failed to approve abstract quotation");
      }
      return;
    }
    if (type === "Purchase Order") {
      const purchaseOrderId = poList?.[0]?.purchase_order_id;
      if (!purchaseOrderId) {
        toast.error("Purchase order not found");
        return;
      }
      try {
        await adminServices.storeProcedure2('approvePurchaseOrder', {
          purchaseOrderId: Number(purchaseOrderId),
          status: "APPROVED",
          remarks: ""
        });
        toast.success("Purchase Order approved");
        await fetchOrderDetails();
        await fetchPO();
      } catch (error) {
        console.error("Failed to approve Purchase Order:", error);
        toast.error(error.response?.data?.message || "Failed to approve Purchase Order");
      }
      return;
    }
    toast.info(`${type} approval functionality coming soon`);
  };

  const handleReject = async (type) => {
    if (type === "Abstract") {
      const purchaseQuotationId = orderDetails?.managerApprovals?.[0]?.purchaseQuotationId;
      const prNo = orderDetails?.managerApprovals?.[0]?.prNo;
      if (!purchaseQuotationId) {
        toast.error("Quotation request ID not found");
        return;
      }
      try {
        const payload = {
          purchaseQuotationId,
          prNo: prNo || "",
          status: "REJECTED",
          approvalRemarks: abstractApprovalRemarks || ""
        };
        await adminServices.approveQuotationRequest(payload);
        toast.success("Abstract quotation rejected");
        await fetchOrderDetails();
        await fetchRFQ();
      } catch (error) {
        console.error("Failed to reject abstract quotation:", error);
        toast.error(error.response?.data?.message || "Failed to reject abstract quotation");
      }
      return;
    }
    if (type === "Purchase Order") {
      const purchaseOrderId = poList?.[0]?.purchase_order_id;
      if (!purchaseOrderId) {
        toast.error("Purchase order not found");
        return;
      }
      try {
        await adminServices.storeProcedure2('approvePurchaseOrder', {
          purchaseOrderId: Number(purchaseOrderId),
          status: "REJECTED",
          remarks: ""
        });
        toast.success("Purchase Order rejected");
        await fetchOrderDetails();
        await fetchPO();
      } catch (error) {
        console.error("Failed to reject Purchase Order:", error);
        toast.error(error.response?.data?.message || "Failed to reject Purchase Order");
      }
      return;
    }
    toast.info(`${type} rejection functionality coming soon`);
  };

  const handleSaveRemarksForMerchant = async () => {
    try {
      setSavingRemarksForMerchant(true);
      await adminServices.storeProcedure2('saveOrderRemarks', {
        orderId: Number(orderId),
        remarks: remarksForMerchant
      });
      toast.success("Remarks saved successfully");
    } catch (error) {
      console.error("Failed to save remarks:", error);
      toast.error(error.response?.data?.message || "Failed to save remarks");
    } finally {
      setSavingRemarksForMerchant(false);
    }
  };

  const handleSaveDeliveryEta = async () => {
    if (!selectedPoIdForDelivery) {
      toast.warning("Please select a PO first");
      return;
    }
    if (!deliveryEtaInput) {
      toast.warning("Please enter a delivery ETA");
      return;
    }
    try {
      setSavingDeliveryEta(true);
      await adminServices.storeProcedure2('updateDeliveryEta', {
        orderId: Number(orderId),
        purchaseOrderId: Number(selectedPoIdForDelivery),
        deliveryEta: deliveryEtaInput
      });
      toast.success("Delivery ETA updated successfully");
      await fetchOrderDetails();
    } catch (error) {
      console.error("Failed to update delivery ETA:", error);
      toast.error(error.response?.data?.message || "Failed to update delivery ETA");
    } finally {
      setSavingDeliveryEta(false);
    }
  };

  const handleCreateDraft = async (section) => {
    if (section === "Purchase Order") {
      try {
        const managerApproval = orderDetails?.managerApprovals?.[0];
        if (!managerApproval) {
          toast.error("Manager approval data not found");
          return;
        }

        setSaving(true);
        const payload = {
          purchaseOrderId: 0,
          ipxOrderId: order?.orderId || order?.id || 0,
          purchaserId: managerApproval.approvedBy || 0,
          deliveryEta: new Date().toISOString(),
          paymentTerms: managerApproval.paymentTerms || "",
          isDraft: true,
          poRemarks: remarks || "",
          items: managerApproval.items
        };

        await adminServices.createPurchaseOrderDraft(payload);
        toast.success("Purchase Order draft created");
        await fetchOrderDetails();
        await fetchPO();
      } catch (error) {
        console.error("Failed to create purchase order draft:", error);
        toast.error(error.response?.data?.message || "Failed to create purchase order draft");
      } finally {
        setSaving(false);
      }
      return;
    }
    if (section === "Delivery Note") {
      setShowDeliveryNoteModal(true);
      return;
    }
    if (section === "Sales Order") {
      setShowSalesOrderModal(true);
      return;
    }
    toast.info(`Create ${section} functionality coming soon`);
  };

  const handleEditDeliveryNote = async (dn) => {
    const dnId = dn?.delivery_note_id ?? dn?.deliveryNoteId ?? dn?.id;
    if (!dnId) {
      toast.error("Delivery Note ID is missing");
      return;
    }
    try {
      setSaving(true);
      const res = await adminServices.getDeliveryNoteDetails(dnId);
      const data = res?.data?.data ?? res?.data?.result ?? res?.data?.results ?? res?.data;
      let editPO = null;

      if (Array.isArray(data) && data.length > 0) {
        const first = data[0];
        const items = data.map((row) => ({
          productId: row.product_id ?? row.productId,
          sku: row.sku,
          description: row.product_name ?? row.description ?? row.item_name ?? row.productName,
          uomName: row.uom_name ?? row.uomName ?? row.uom,
          quantity: row.quantity ?? row.qty,
          unitPrice: row.price ?? row.unitPrice ?? row.cost ?? row.unit_price,
          deliveredQty: row.quantity ?? row.qty,
          ...row,
        }));
        editPO = {
          ...first,
          purchase_order_id: first.purchase_order_id ?? first.purchaseOrderId,
          items,
          inspectedBy: first.inspected_by,
          notedBy: first.noted_by,
          receivedBy: first.received_by,
          remarks: first.remarks,
        };
      } else if (data && typeof data === 'object') {
        editPO = {
          ...data,
          purchase_order_id: data.purchase_order_id ?? data.purchaseOrderId,
          items: Array.isArray(data.items) ? data.items : [],
          inspectedBy: data.inspected_by,
          notedBy: data.noted_by,
          receivedBy: data.received_by,
          remarks: data.remarks,
        };
      }

      if (!editPO) {
        toast.error("Failed to load Delivery Note details");
        return;
      }

      setEditDeliveryNoteId(Number(dnId));
      setDeliveryNotePO(editPO);
      setShowEditDeliveryNoteModal(true);
    } catch (error) {
      console.error("Failed to fetch Delivery Note details:", error);
      toast.error(error.response?.data?.message || "Failed to load Delivery Note details");
    } finally {
      setSaving(false);
    }
  };

  const handleEditSalesOrder = async (so) => {
    const soId = so?.sales_order_id ?? so?.salesOrderId ?? so?.id;
    if (!soId) {
      toast.error("Sales Order ID is missing");
      return;
    }
    try {
      setSaving(true);
      const res = await adminServices.getSalesOrderDetails(soId);
      const data = res?.data?.data ?? res?.data?.result ?? res?.data?.results ?? res?.data;
      let editSO = null;

      if (Array.isArray(data) && data.length > 0) {
        const first = data[0];
        const items = data.map((row) => ({
          productId: row.product_id ?? row.productId,
          sku: row.sku,
          description: row.product_name ?? row.description ?? row.item_name ?? row.productName,
          uomName: row.uom_name ?? row.uomName ?? row.uom,
          quantity: row.quantity ?? row.qty,
          unitPrice: row.price ?? row.unitPrice ?? row.cost ?? row.unit_price,
          ...row,
        }));
        editSO = {
          ...first,
          sales_order_id: first.sales_order_id ?? first.salesOrderId ?? soId,
          purchase_order_id: first.purchase_order_id ?? first.purchaseOrderId,
          items,
          processedBy: first.processed_by,
          processed_by: first.processed_by,
          certifiedCorrect: first.certified_correct,
          certified_correct: first.certified_correct,
          receivedPayment: first.received_payment,
          received_payment: first.received_payment,
          remarks: first.remarks,
          merchantReservedFunds: first.merchant_reserved_funds,
          balanceAfter: first.balance_after,
        };
      } else if (data && typeof data === 'object') {
        editSO = {
          ...data,
          sales_order_id: data.sales_order_id ?? data.salesOrderId ?? soId,
          purchase_order_id: data.purchase_order_id ?? data.purchaseOrderId,
          items: Array.isArray(data.items) ? data.items : [],
          processedBy: data.processed_by,
          processed_by: data.processed_by,
          certifiedCorrect: data.certified_correct,
          certified_correct: data.certified_correct,
          receivedPayment: data.received_payment,
          received_payment: data.received_payment,
          remarks: data.remarks,
          merchantReservedFunds: data.merchant_reserved_funds,
          balanceAfter: data.balance_after,
        };
      }

      if (!editSO) {
        toast.error("Failed to load Sales Order details");
        return;
      }

      setEditSalesOrderId(Number(soId));
      setSalesOrderDN(editSO);
      setShowEditSalesOrderModal(true);
    } catch (error) {
      console.error("Failed to fetch Sales Order details:", error);
      toast.error(error.response?.data?.message || "Failed to load Sales Order details");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveChanges = async () => {
    try {
      // Validate that at least one item is selected
      if (Object.keys(selectedItems).length === 0) {
        toast.warning("Please select at least one supplier for the items");
        return;
      }

      // Get quotation request ID from orderDetails
      const quotationRequestId = orderDetails?.managerApprovals?.[0]?.purchaseQuotationId;
      if (!quotationRequestId) {
        toast.error("Quotation request ID not found");
        return;
      }

      setSaving(true);

      // Build payload from selected items
      const items = [];
      Object.entries(selectedItems).forEach(([itemIndex, supplierIndex]) => {
        const item = supplierData[parseInt(itemIndex)];
        const supplier = suppliers[supplierIndex];
        const supplierItem = item.suppliers[supplierIndex];
        
        //if (supplierItem.isAvailable) {
          items.push({
            productId: item.productId,
            supplierId: supplier.supplierId,
            uomId: item.uomId,
            quantity: item.qty,
            unitPrice: supplierItem.costPrice,
            subtotal: supplierItem.total,
            remarks: ""
          });
        //}
      });

      if (items.length === 0) {
        toast.warning("No available items selected");
        setSaving(false);
        return;
      }

      const payload = {
        supplierRecommendationRemarks: remarks,
        items: items
      };

      //console.log("Saving quotation request items:", payload);

      await adminServices.updateQuotationRequestItems(quotationRequestId, payload);
      
      toast.success("Changes saved successfully");
      
      // Refresh order details
      await fetchOrderDetails();
    } catch (error) {
      console.error("Failed to save changes:", error);
      toast.error(error.response?.data?.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateRFQ = async () => {
    try {
      // If RFQ already exists, do not generate again
      if (rfq) {
        toast.info("RFQ already generated");
        return;
      }
      // Validate that at least one item is selected
      if (Object.keys(selectedItems).length === 0) {
        toast.warning("Please select at least one supplier for the items");
        return;
      }

      // Get quotation request ID from orderDetails
      const quotationRequestId = orderDetails?.managerApprovals?.[0]?.purchaseQuotationId;
      if (!quotationRequestId) {
        toast.error("Quotation request ID not found");
        return;
      }

      // Determine the most frequently selected supplier
      const supplierCounts = {};
      Object.values(selectedItems).forEach(supplierIndex => {
        supplierCounts[supplierIndex] = (supplierCounts[supplierIndex] || 0) + 1;
      });

      // Get the supplier index with the highest count
      const mostSelectedSupplierIndex = Object.entries(supplierCounts)
        .sort((a, b) => b[1] - a[1])[0][0];
      
      const selectedSupplierId = suppliers[parseInt(mostSelectedSupplierIndex)]?.supplierId;
      
      if (!selectedSupplierId) {
        toast.error("Unable to determine selected supplier");
        return;
      }

      setSaving(true);
      
      const payload = {
        supplierId: selectedSupplierId
      };

      //console.log("Generating RFQ with payload:", payload);
      
      const response = await adminServices.generateRFQPdf(quotationRequestId, payload);
      
      toast.success("RFQ PDF generated successfully");
      //console.log("RFQ PDF response:", response.data);
      
      // Refresh order details to get updated data
      await fetchOrderDetails();
    } catch (error) {
      console.error("Failed to generate RFQ:", error);
      //toast.error(error.response?.data?.message || "Failed to generate RFQ PDF");
    } finally {
      setSaving(false);
    }
  };

  const handleSupplierSelect = (itemIndex, supplierIndex) => {
    const item = supplierData[itemIndex];
    const supplier = suppliers[supplierIndex];
    const supplierItem = item?.suppliers?.[supplierIndex];
    
    // Toggle selection: only one supplier per item
    setSelectedItems(prev => ({
      ...prev,
      [itemIndex]: supplierIndex
    }));
    
    toast.success(`Selected ${supplier.supplierName} for ${item.description}`);
  };

  const handleDeleteItem = (itemIndex) => {
    toast.info(`Delete item ${itemIndex + 1} functionality coming soon`);
  };

  // Parse supplier data from JSON string
  const parseSupplierData = () => {
    try {
        //console.log("Raw supplier data string:", orderDetails);
      const jsonString = orderDetails?.managerApprovals?.[0]?.supplierData;
      if (!jsonString) return { supplierData: [], suppliers: [] };

      const parsed = JSON.parse(jsonString);
      //console.log("Parsed supplier data:", parsed);
      const recommendations = parsed.recommendations || [];
      
      if (recommendations.length === 0) {
        return { supplierData: [], suppliers: [] };
      }

      // Get all unique products across all suppliers
      const productMap = new Map();
      
      recommendations.forEach((recommendation, supplierIndex) => {
        recommendation.products?.forEach(product => {
          const key = product.productId;
          if (!productMap.has(key)) {
            productMap.set(key, {
              productId: product.productId,
              sku: `${product.productId}`,
              description: product.productName,
              uom: product.uomName,
              uomId: product.uomId,
              qty: product.quantityRequested,
              suppliers: []
            });
          }
          
          // Add supplier data for this product
          const item = productMap.get(key);
          item.suppliers[supplierIndex] = {
            costPrice: product.unitPrice,
            total: product.subtotal,
            selected: false,
            isAvailable: product.isAvailable
          };
        });
      });

      // Convert map to array and fill missing supplier slots
      const supplierData = Array.from(productMap.values()).map(item => {
        // Ensure all supplier slots exist
        const suppliers = [];
        for (let i = 0; i < recommendations.length; i++) {
          suppliers[i] = item.suppliers[i] || {
            costPrice: 0,
            total: 0,
            selected: false,
            isAvailable: false
          };
        }
        return { ...item, suppliers };
      });

      // Get supplier information
      const suppliers = recommendations.map(rec => ({
        supplierId: rec.supplierId,
        supplierName: rec.supplierName,
        contactName: rec.contactName,
        rank: rec.rank
      }));

      //console.log("Transformed supplier data:", { supplierData, suppliers });
      return { supplierData, suppliers };
    } catch (error) {
      console.error("Failed to parse supplier data:", error);
      return { supplierData: [], suppliers: [] };
    }
  };

  const { supplierData, suppliers } = parseSupplierData();

  // Fetch and enrich supplier details
 

  // Calculate grand total for each supplier
  const calculateSupplierTotal = (supplierIndex) => {
    return supplierData.reduce((sum, item) => {
      const supplier = item.suppliers?.[supplierIndex];
      return sum + (supplier?.total || 0);
    }, 0);
  };

  if (loading) {
    return (
      <div className="order-information-page">
        <div className="order-information-header">
          <button className="order-information-back-btn" onClick={onClose}>
            <ArrowLeft size={20} />
          </button>
          <h1 className="order-information-title">Back to Order Management</h1>
        </div>
        <div style={{ padding: "40px", textAlign: "center" }}>Loading...</div>
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="order-information-page">
        <div className="order-information-header">
          <button className="order-information-back-btn" onClick={onClose}>
            <ArrowLeft size={20} />
          </button>
          <h1 className="order-information-title">Back to Order Management</h1>
        </div>
        <div style={{ padding: "40px", textAlign: "center" }}>Order not found</div>
      </div>
    );
  }

  const { order, orderItems, activityLogs } = orderDetails;

  const rfqStatus = String(order?.rfqStatus || "").toUpperCase();

  const hideCreatePO = rfqStatus === "PENDING" || rfqStatus === "REJECTED";
  const disablePOApproval = hideCreatePO;

  // Determine PO readiness for Delivery Note creation based on actual PO list
  const hasApprovedPO = poList.some((po) => {
    const status = String(po.status || "").toUpperCase();
    return status && status !== "PENDING" && status !== "REJECTED" && status !== "DRAFT";
  });
  const hasAnyPO = poList && poList.length > 0;
  const showCreateDeliveryNote = hasApprovedPO;

  // Determine DN readiness for Sales Order creation based on actual DN list
  const hasApprovedDN = deliveryNotesList.some((dn) => {
    const status = String(dn.status || "").toUpperCase();
    const isDraft =
      status === "DRAFT" ||
      status === "PENDING" ||
      dn.is_draft == 1 ||
      dn.is_draft === true ||
      dn.isDraft == 1 ||
      dn.isDraft === true;
    return !isDraft;
  });
  const hasAnyDN = deliveryNotesList && deliveryNotesList.length > 0;
  const showCreateSalesOrder = hasApprovedDN;
  
  const abstractApprovalStatus = String(orderDetails?.managerApprovals?.[0]?.status || "").toUpperCase();
  const hideAbstractApprovalButtons = abstractApprovalStatus === "APPROVED" || abstractApprovalStatus === "REJECTED";

  return (
    <div className="order-information-page">
      <div className="order-information-header">
        <button className="order-information-back-btn" onClick={onClose}>
          <ArrowLeft size={20} />
        </button>
        <div className="process-order-header-content">
          <div className="process-order-title-section">
            <h1 className="order-information-title">{order.orderNo}</h1>
            <span className="process-order-status-badge">{order.status}</span>
          </div>
        </div>
      </div>

      <div className="process-order-content">
        <div className="process-order-layout">
          {/* Left Column */}
          <div className="process-order-left">
            {/* Merchant Reserved Funds */}
            <div className="process-merchant-funds">
              <label>Merchant Reserved Funds <span className="info-icon">ⓘ</span></label>
              <div className="process-merchant-funds-amount">
                {merchantWallet ? formatCurrency(merchantWallet.wallet_amount) : "Loading..."}
              </div>
            </div>

            {/* Order Information */}
            <div className="process-section">
              <h3 className="process-section-title">Order Information</h3>
              <div className="process-info-grid">
                <div className="process-info-field">
                  <label>Order ID</label>
                  <div>{order.orderNo}</div>
                </div>
                <div className="process-info-field">
                  <label>Order Date</label>
                  <div>{formatDate(order.regDate)}</div>
                </div>
                <div className="process-info-field">
                  <label>Regular Order</label>
                  <div>{order.type}</div>
                </div>
                <div className="process-info-field">
                  <label>Merchant Account</label>
                  <div>{order.accountType}</div>
                </div>
                <div className="process-info-field">
                  <label>Name</label>
                  <div>{order.client ? `${order.client.firstName} ${order.client.lastName}` : "-"}</div>
                </div>
                <div className="process-info-field">
                  <label>PDL Number</label>
                  <div>{order.client?.pdlNumber || "-"}</div>
                </div>
                <div className="process-info-field">
                  <label>Location</label>
                  <div>{order.colony?.colonyName || "-"}</div>
                </div>
                <div className="process-info-field">
                  <label>Store Code</label>
                  <div>{order.merchant?.id || "-"}</div>
                </div>
              </div>
            </div>

            {/* Manager Approval */}
            <div className="process-section">
              <h3 className="process-section-title">Manager Approval</h3>
              <div className="process-approval-field">
                <label>Abstract of Quotation</label>
                {!hideAbstractApprovalButtons && (
                  <div className="process-approval-actions">
                    <button className="process-approve-btn" onClick={() => handleApprove("Abstract")}>
                      ✓ Approve
                    </button>
                    <button className="process-reject-btn" onClick={() => handleReject("Abstract")}>
                      ✗ Reject
                    </button>
                  </div>
                )}
                <textarea
                  placeholder="Comment..."
                  className="process-textarea"
                  value={abstractApprovalRemarks}
                  onChange={(e) => setAbstractApprovalRemarks(e.target.value)}
                ></textarea>
              </div>
              <div className="process-approval-field">
                <label>Purchase Order</label>
                <div className="process-approval-actions">
                  <button className="process-approve-btn" onClick={() => handleApprove("Purchase Order")} disabled={disablePOApproval}>
                    ✓ Approve
                  </button>
                  <button className="process-reject-btn" onClick={() => handleReject("Purchase Order")} disabled={disablePOApproval}>
                    ✗ Reject
                  </button>
                </div>
                <textarea placeholder="Comment..." className="process-textarea" disabled></textarea>
              </div>
            </div>

            {/* Delivery Info */}
            <div className="process-section">
              <h3 className="process-section-title">Delivery Info</h3>
              <div className="process-info-field">
                <label>SELECT A PO</label>
                <select
                  className="process-select"
                  value={selectedPoIdForDelivery}
                  onChange={(e) => setSelectedPoIdForDelivery(e.target.value)}
                >
                  <option value="">Select a PO...</option>
                  {poList && poList.map((po, index) => (
                    <option key={index} value={po.purchase_order_id}>
                      {po.filename}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="process-info-field">
                <label>Delivery ETA</label>
                <input
                  type="date"
                  className="process-input"
                  value={deliveryEtaInput}
                  onChange={(e) => setDeliveryEtaInput(e.target.value)}
                />
              </div>
              <div className="process-eta-note">
                📅 ETA per Purchase Order
              </div>
              <button
                className="process-create-btn"
                style={{ marginTop: 8 }}
                onClick={handleSaveDeliveryEta}
                disabled={savingDeliveryEta}
              >
                {savingDeliveryEta ? "Saving..." : "Save ETA"}
              </button>
            </div>

            {/* Activity Logs */}
            <div className="process-section">
              <h3 className="process-section-title">Activity Logs</h3>
              <div className="process-activity-logs">
                {activityLogs && activityLogs.length > 0 ? (
                  activityLogs.map((log, index) => (
                    <div key={index} className="process-activity-item">
                      <div className="process-activity-time">
                        {formatDate(log.regDate)}
                      </div>
                      <div className="process-activity-action">
                        {log.action}{log.description ? ` (${log.description})` : ""}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="process-activity-empty">No activity logs available</div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="process-order-right">
            {/* Abstract of Quotation */}
            <div className="process-document-section process-abstract-section">
              <h3 className="process-document-title">Abstract of Quotation</h3>
              
              {supplierData.length > 0 ? (
                <>
                  {/* Supplier Recommendation Remarks */}
                  <div className="process-remarks-field" style={{ marginBottom: "16px" }}>
                    <label style={{ fontSize: "0.875rem", fontWeight: "500", marginBottom: "8px", display: "block" }}>
                      Supplier Recommendation Remarks
                    </label>
                    <textarea 
                      className="process-textarea"
                      placeholder="Enter recommendations or notes..."
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      rows={3}
                      style={{ width: "100%", resize: "vertical" }}
                    />
                  </div>

                  <div className="process-supplier-table-wrapper">
                    <table className="process-supplier-table">
                      <thead>
                        <tr>
                          <th rowSpan="2">Item Description</th>
                          <th rowSpan="2">UOM</th>
                          <th rowSpan="2">QTY</th>
                          {suppliers.map((supplier, index) => {
                            const enriched = enrichedSuppliers[supplier.supplierId] || supplier;
                            return (
                              <th key={index} colSpan="3" className="supplier-header" title={`${enriched.supplierName}\nContact: ${enriched.contactName || 'N/A'}\nPhone: ${enriched.phone || 'N/A'}\nEmail: ${enriched.email || 'N/A'}`}>
                                <div style={{ fontSize: '0.95rem', fontWeight: 600 }}>
                                  {enriched.supplierName || `Supplier ${index + 1}`}
                                </div>
                                {enriched.contactName && <div style={{ fontSize: '0.75rem', color: '#666' }}>Contact: {enriched.contactName}</div>}
                                {enriched.phone && <div style={{ fontSize: '0.75rem', color: '#666' }}>{enriched.phone}</div>}
                                <button className="supplier-delete-btn">🗑️</button>
                              </th>
                            );
                          })}
                        </tr>
                        <tr>
                          {suppliers.map((_, index) => (
                            <React.Fragment key={index}>
                              <th className="supplier-subheader">Cost Price</th>
                              <th className="supplier-subheader">Total</th>
                              <th className="supplier-subheader"></th>
                            </React.Fragment>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {supplierData.map((item, itemIndex) => (
                          <tr key={itemIndex}>
                            <td>
                              <div className="item-description">
                                <strong>{item.sku}</strong> {item.description}
                              </div>
                            </td>
                            <td>{item.uom}</td>
                            <td>{item.qty}</td>
                            {item.suppliers?.map((supplier, supplierIndex) => (
                              <React.Fragment key={supplierIndex}>
                                <td>
                                  <input 
                                    type="text" 
                                    className="supplier-input"
                                    value={supplier.costPrice ? `₱${supplier.costPrice}` : '₱0'}
                                    readOnly
                                    style={{ 
                                      backgroundColor: !supplier.isAvailable ? '#fee' : '#f9fafb',
                                      color: !supplier.isAvailable ? '#999' : '#000'
                                    }}
                                  />
                                </td>
                                <td>
                                  <strong>{supplier.total ? formatCurrency(supplier.total) : '₱0'}</strong>
                                </td>
                                <td className="supplier-checkbox-cell">
                                  <input 
                                    type="checkbox" 
                                    checked={selectedItems[itemIndex] === supplierIndex}
                                    onChange={() => handleSupplierSelect(itemIndex, supplierIndex)}
                                    // disabled={!supplier.isAvailable}
                                  />
                                </td>
                              </React.Fragment>
                            ))}
                            <td className="item-delete-cell">
                              <button 
                                className="item-delete-btn"
                                onClick={() => handleDeleteItem(itemIndex)}
                              >
                                🗑️
                              </button>
                            </td>
                          </tr>
                        ))}
                        
                        {/* Grand Total Row */}
                        <tr className="grand-total-row">
                          <td colSpan="3" className="grand-total-label">
                            <strong>GRAND TOTAL</strong>
                          </td>
                          {suppliers.map((_, supplierIndex) => (
                            <React.Fragment key={supplierIndex}>
                              <td></td>
                              <td>
                                <strong>{formatCurrency(calculateSupplierTotal(supplierIndex))}</strong>
                              </td>
                              <td></td>
                            </React.Fragment>
                          ))}
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="process-abstract-actions">
                    <button 
                      className="process-abstract-save-btn" 
                      onClick={handleSaveChanges}
                      disabled={saving}
                    >
                      {saving ? "Saving..." : "Save Changes"}
                    </button>
                    <button 
                      className="process-abstract-rfq-btn" 
                      onClick={handleGenerateRFQ}
                      disabled={saving || Boolean(rfq)}
                      title={rfq ? "RFQ already generated" : undefined}
                    >
                      {saving ? "Generating..." : (rfq ? "RFQ Generated" : "Generate RFQ")}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="process-document-placeholder">
                    No Abstract of Quotation created yet
                  </div>
                  <div className="process-abstract-actions">
                    <button 
                      className="process-abstract-save-btn" 
                      onClick={handleSaveChanges}
                      disabled={saving}
                    >
                      {saving ? "Saving..." : "Save Changes"}
                    </button>
                    <button 
                      className="process-abstract-rfq-btn" 
                      onClick={handleGenerateRFQ}
                      disabled={saving}
                    >
                      {saving ? "Generating..." : "Generate RFQ"}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Request for Quotation */}
            <div className="process-document-section">
              <h3 className="process-document-title">Request for Quotation</h3>
              {rfqLoading ? (
                <div style={{ padding: 12, color: "#6b7280" }}>Loading RFQ...</div>
              ) : rfq ? (
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ flex: 1 }}>{rfq.filename || rfq.FileName || "RFQ.pdf"}</div>
                  <div>
                    <button
                      className="process-create-btn"
                      onClick={async () => {
                        try {
                          // Prefer quotation request id if available
                          const quotationRequestId = rfq.quotation_request_id ?? rfq.quotationRequestId ?? rfq.quotation_requestid ?? rfq.quotationid;
                          const payload = {};
                          let resp;
                          if (quotationRequestId) {
                            await adminServices.downloadRFQPdf(quotationRequestId,rfq.filename);
                          } else {
                            // Fallback to direct attachment endpoint if no quotationRequestId
                            //resp = await adminServices.getRFQAttachment(rfq.attachment_id ?? rfq.attachmentId ?? rfq.attachmentid);
                          }

                          
                        } catch (err) {
                          console.error('Failed to download RFQ:', err);
                          toast.error('Failed to download RFQ');
                        }
                      }}
                    >
                      Download
                    </button>
                  </div>
                </div>
              ) : (
                <div className="process-document-placeholder">
                  No Request for Quotation created yet
                </div>
              )}
            </div>

            {/* Purchase Order */}
            <div className="process-document-section">
              <h3 className="process-document-title">Purchase Order</h3>
              {poLoading ? (
                <div style={{ padding: 12, color: "#6b7280" }}>Loading PO...</div>
              ) : poList && poList.length > 0 ? poList.map((po, idx) => (
                <div key={idx} style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>{po.filename || po.FileName || "PO.pdf"} ({po.status})</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <DownloadIcon size={16} cursor="pointer" onClick={async () => {
                        try {
                          await adminServices.downloadPOPdf(po.purchase_order_id ?? po.purchaseOrderId, po.filename);
                        } catch (err) {
                          console.error('Failed to download PO:', err);
                          toast.error('Failed to download PO');
                        }
                      }} />
                    <PenBoxIcon size={16} cursor="pointer" color="blue" onClick={async () => {
                        setPoNo(po);
                        setPoDetailsLoading(true);
                        try {
                          const response = await adminServices.getSupplierById(po.supplierId);
                          setSupplier(response.data[0]);
                        } catch (err) {
                          console.error('Failed to load supplier:', err);
                          setPoDetailsLoading(false);
                        }
                        setEditingPO(true);
                      }} />
                  </div>
                </div>
              )) : (
                <div className="process-document-placeholder">
                  No Purchase Order created yet
                </div>
              )}
              {!hideCreatePO && poList.length === 0 && (
                <button className="process-create-btn" onClick={() => handleCreateDraft("Purchase Order")}>
                  Create Draft
                </button>
              )}
            </div>

            <div className="process-document-section">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h3 className="process-document-title" style={{ margin: 0 }}>Delivery Note</h3>
                {showCreateDeliveryNote && (
                  <button className="process-create-btn" onClick={() => handleCreateDraft("Delivery Note")}>
                    Create Draft
                  </button>
                )}
              </div>
              {!hasAnyPO && (
                <div className="process-document-placeholder">
                  Create a Purchase Order first to enable Delivery Notes.
                </div>
              )}
              {hasAnyPO && !showCreateDeliveryNote && (
                <div className="process-document-placeholder">
                  Purchase Order must be approved before creating a Delivery Note.
                </div>
              )}
              {hasAnyPO && showCreateDeliveryNote && (
                deliveryNotesList.length > 0 ? (
                  deliveryNotesList.map((dn, idx) => {
                    const isDraft =
                      String(dn.status || "").toUpperCase() === "DRAFT" ||
                      String(dn.status || "").toUpperCase() === "PENDING" ||
                      dn.is_draft == 1 ||
                      dn.is_draft === true ||
                      dn.isDraft == 1 ||
                      dn.isDraft === true;
                    // eslint-disable-next-line no-console
                    console.log("[DN list] dn:", dn, "isDraft:", isDraft);
                    return (
                      <div key={idx} style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 8 }}>
                        <div style={{ flex: 1 }}>{dn.filename || dn.FileName || `DN-${idx + 1}.pdf`} ({dn.status || "-"})</div>
                        {isDraft && (
                          <button
                            className="process-create-btn"
                            style={{ padding: "4px 8px", fontSize: 12 }}
                            onClick={() => handleEditDeliveryNote(dn)}
                            type="button"
                          >
                            Edit
                          </button>
                        )}
                        <DownloadIcon size={16} style={{ cursor: "pointer" }} onClick={async () => {
                          try {
                            const dnId = dn.delivery_note_id ?? dn.deliveryNoteId ?? dn.id;
                            if (dnId) {
                              await adminServices.downloadDeliveryNote(Number(dnId));
                            }
                          } catch (err) {
                            toast.error('Failed to download Delivery Note');
                          }
                        }} />
                      </div>
                    );
                  })
                ) : (
                  <div className="process-document-placeholder">
                    No Delivery Note created yet
                  </div>
                )
              )}
            </div>

            <div className="process-document-section">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h3 className="process-document-title" style={{ margin: 0 }}>Sales Order</h3>
                {showCreateSalesOrder && (
                  <button className="process-create-btn" onClick={() => handleCreateDraft("Sales Order")}>
                    Create Draft
                  </button>
                )}
              </div>
              {!hasAnyDN && (
                <div className="process-document-placeholder">
                  Create a Delivery Note first to enable Sales Orders.
                </div>
              )}
              {hasAnyDN && !showCreateSalesOrder && (
                <div className="process-document-placeholder">
                  Delivery Note must be submitted before creating a Sales Order.
                </div>
              )}
              {hasAnyDN && showCreateSalesOrder && (
                salesOrdersList.length > 0 ? (
                  salesOrdersList.map((so, idx) => {
                    const soIsDraft =
                      String(so.status || "").toUpperCase() === "DRAFT" ||
                      String(so.status || "").toUpperCase() === "PENDING" ||
                      so.is_draft == 1 ||
                      so.is_draft === true ||
                      so.isDraft == 1 ||
                      so.isDraft === true;
                    return (
                      <div key={idx} style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 8 }}>
                        <div style={{ flex: 1 }}>{so.filename || so.FileName || `SO-${idx + 1}.pdf`} ({so.status || "-"})</div>
                        {soIsDraft && (
                          <button
                            className="process-create-btn"
                            style={{ padding: "4px 8px", fontSize: 12 }}
                            onClick={() => handleEditSalesOrder(so)}
                            type="button"
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="process-document-placeholder">
                    No Sales Order created yet
                  </div>
                )
              )}
            </div>

            {/* Remarks for Merchant */}
            <div className="process-remarks-section">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <h3 className="process-document-title" style={{ margin: 0 }}>Remarks for Merchant</h3>
                <button
                  className="process-create-btn"
                  onClick={handleSaveRemarksForMerchant}
                  disabled={savingRemarksForMerchant}
                >
                  {savingRemarksForMerchant ? "Saving..." : "Save"}
                </button>
              </div>
              <textarea
                className="process-remarks-textarea"
                placeholder="Enter remarks..."
                value={remarksForMerchant}
                onChange={(e) => setRemarksForMerchant(e.target.value)}
              ></textarea>
            </div>
          </div>
        </div>

        {/* Bottom Actions */}
        <div className="process-order-bottom-actions">
          <button className="process-save-btn" onClick={handleSave}>
            Save
          </button>
          <button className="process-cancel-btn" onClick={handleCancel}>
            Cancel
          </button>
        </div>
      </div>

      {/* Edit Purchase Order Modal */}
      <EditPOModal
        isOpen={editingPO}
        poDetails={poDetails}
        orderDetails={orderDetails}
        onClose={() => setEditingPO(false)}
        onSaveDraft={async (updatedPO) => {
          if(updatedPO){
            try {
              setSaving(true);
              const payload = {
                purchaseOrderId: poNo.purchase_order_id,
                isDraft: true,
                poRemarks: updatedPO.remarks || ""
              };
              await adminServices.updateData(`ipxpurchaseorders/as-draft/${poNo.purchase_order_id}`,payload);
              toast.success("Purchase Order saved as draft");
              await fetchOrderDetails();
              await fetchPO();
            } catch (error) {
              console.error("Error saving PO draft:", error);
              toast.error("Failed to save PO draft");
            }
          }
        }}
        onFinalize={async (updatedPO) => {
          try {
            setSaving(true);
            await adminServices.finalizePurchaseOrder(poNo.purchase_order_id);
            toast.success('Purchase Order finalized');
            await fetchOrderDetails();
            await fetchPO();
            setEditingPO(false);
          } catch (error) {
            console.error('Error finalizing PO:', error);
            toast.error(error.response?.data?.message || 'Failed to finalize Purchase Order');
          } finally {
            setSaving(false);
          }
        }}
      />

      {/* Create Delivery Note Modal */}
      <CreateDeliveryNoteModal
        isOpen={showDeliveryNoteModal}
        poList={poList}
        onClose={() => setShowDeliveryNoteModal(false)}
        onProceed={async (selectedPO) => {
          const selectedId = selectedPO?.id ?? selectedPO?.purchase_order_id ?? selectedPO?.purchaseOrderId;
          if (!selectedId) {
            toast.error("No Purchase Order selected");
            return;
          }

          let fullPO = orderDetails?.purchaseOrders?.find((po) => {
            const poId = po?.purchase_order_id ?? po?.purchaseOrderId ?? po?.id;
            return poId == selectedId;
          }) || selectedPO;

          try {
            setPoLoading(true);
            // eslint-disable-next-line no-console
            console.log("[DN] Fetching PO details for selectedId:", selectedId, "selectedPO:", selectedPO);
            const res = await adminServices.getPurchaseOrderDetails(selectedId);
            // eslint-disable-next-line no-console
            console.log("[DN] getPurchaseOrderDetails response:", res);
            const data = res?.data?.data ?? res?.data?.result ?? res?.data?.results ?? res?.data;

            if (Array.isArray(data) && data.length > 0) {
              const first = data[0];
              const items = data.map((row) => ({
                productId: row.product_id ?? row.productId,
                sku: row.sku,
                description: row.product_name ?? row.description ?? row.item_name ?? row.productName,
                uomName: row.uom_name ?? row.uomName ?? row.uom,
                quantity: row.quantity ?? row.qty,
                unitPrice: row.price ?? row.unitPrice ?? row.cost ?? row.unit_price,
                ...row,
              }));
              fullPO = {
                ...fullPO,
                ...first,
                purchase_order_id: first.purchase_order_id ?? first.purchaseOrderId ?? selectedId,
                items,
              };
            } else if (data && typeof data === 'object') {
              fullPO = {
                ...fullPO,
                ...data,
                purchase_order_id: data.purchase_order_id ?? data.purchaseOrderId ?? selectedId,
                items: Array.isArray(data.items) ? data.items : [],
              };
            }
            // eslint-disable-next-line no-console
            console.log("[DN] normalized fullPO:", fullPO);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error("[DN] Failed to fetch PO details:", error);
            toast.error(error.response?.data?.message || "Failed to load PO details");
          } finally {
            setPoLoading(false);
          }

          setDeliveryNotePO(fullPO);
          setShowDeliveryNoteModal(false);
          setShowEditDeliveryNoteModal(true);
        }}
      />

      {/* Edit Delivery Note Modal */}
      <EditDeliveryNoteModal
        isOpen={showEditDeliveryNoteModal}
        selectedPO={deliveryNotePO}
        deliveryNoteId={editDeliveryNoteId}
        orderDetails={orderDetails}
        onClose={() => {
          setShowEditDeliveryNoteModal(false);
          setDeliveryNotePO(null);
          setEditDeliveryNoteId(null);
        }}
        onSaveDraft={async (payload, file) => {
          try {
            setSaving(true);
            if (editDeliveryNoteId) {
              await adminServices.updateDeliveryNote(buildUpdateDeliveryNotePayload(payload, true));
              toast.success('Delivery Note draft updated');
            } else {
              await adminServices.createDeliveryNote(buildDeliveryNotePayload(payload, true));
              toast.success('Delivery Note draft saved');
            }
            await fetchDeliveryNotes();
          } catch (error) {
            console.error('Error saving Delivery Note draft:', error);
            toast.error(error.response?.data?.message || 'Failed to save Delivery Note draft');
            throw error;
          } finally {
            setSaving(false);
          }
        }}
        onSubmit={async (payload, file) => {
          try {
            setSaving(true);
            if (editDeliveryNoteId) {
              await adminServices.updateDeliveryNote(buildUpdateDeliveryNotePayload(payload, false));
              toast.success('Delivery Note updated and submitted');
            } else {
              await adminServices.createDeliveryNote(buildDeliveryNotePayload(payload, false));
              toast.success('Delivery Note submitted');
            }
            await fetchDeliveryNotes();
          } catch (error) {
            console.error('Error submitting Delivery Note:', error);
            toast.error(error.response?.data?.message || 'Failed to submit Delivery Note');
            throw error;
          } finally {
            setSaving(false);
          }
        }}
      />

      {/* Create Sales Order Modal */}
      <CreateSalesOrderModal
        isOpen={showSalesOrderModal}
        dnList={deliveryNotesList}
        onClose={() => setShowSalesOrderModal(false)}
        onProceed={async (selectedDN) => {
          const selectedId = selectedDN?.id ?? selectedDN?.delivery_note_id ?? selectedDN?.deliveryNoteId;
          if (!selectedId) {
            toast.error("No Delivery Note selected");
            return;
          }
          try {
            setSaving(true);
            const res = await adminServices.getDeliveryNoteDetails(selectedId);
            const data = res?.data?.data ?? res?.data?.result ?? res?.data?.results ?? res?.data;
            let fullDN = { ...selectedDN };

            if (Array.isArray(data) && data.length > 0) {
              const first = data[0];
              const items = data.map((row) => ({
                productId: row.product_id ?? row.productId,
                sku: row.sku,
                description: row.product_name ?? row.description ?? row.item_name ?? row.productName,
                uomName: row.uom_name ?? row.uomName ?? row.uom,
                quantity: row.quantity ?? row.qty,
                unitPrice: row.price ?? row.unitPrice ?? row.cost ?? row.unit_price,
                ...row,
              }));
              fullDN = {
                ...fullDN,
                ...first,
                delivery_note_id: first.delivery_note_id ?? first.deliveryNoteId ?? selectedId,
                items,
              };
            } else if (data && typeof data === 'object') {
              fullDN = {
                ...fullDN,
                ...data,
                delivery_note_id: data.delivery_note_id ?? data.deliveryNoteId ?? selectedId,
                items: Array.isArray(data.items) ? data.items : [],
              };
            }

            setSalesOrderDN(fullDN);
            setShowSalesOrderModal(false);
            setShowEditSalesOrderModal(true);
          } catch (error) {
            console.error("Failed to fetch Delivery Note details:", error);
            toast.error(error.response?.data?.message || "Failed to load Delivery Note details");
          } finally {
            setSaving(false);
          }
        }}
      />

      {/* Edit Sales Order Modal */}
      <EditSalesOrderModal
        isOpen={showEditSalesOrderModal}
        selectedDN={salesOrderDN}
        salesOrderId={editSalesOrderId}
        orderDetails={orderDetails}
        onClose={() => {
          setShowEditSalesOrderModal(false);
          setSalesOrderDN(null);
          setEditSalesOrderId(null);
        }}
        onSaveDraft={async (payload, file) => {
          try {
            setSaving(true);
            let res;
            if (editSalesOrderId) {
              res = await adminServices.updateSalesOrder(buildUpdateSalesOrderPayload(payload, true));
              // eslint-disable-next-line no-console
              console.log("[SO] updateSalesOrder draft response:", res);
              toast.success('Sales Order draft updated');
            } else {
              res = await adminServices.createSalesOrder(buildSalesOrderPayload(payload, true));
              // eslint-disable-next-line no-console
              console.log("[SO] createSalesOrder draft response:", res);
              toast.success('Sales Order draft saved');
            }
            await fetchSalesOrders();
          } catch (error) {
            console.error('Error saving Sales Order draft:', error);
            toast.error(error.response?.data?.message || 'Failed to save Sales Order draft');
            throw error;
          } finally {
            setSaving(false);
          }
        }}
        onSubmit={async (payload, file) => {
          try {
            setSaving(true);
            let res;
            if (editSalesOrderId) {
              res = await adminServices.updateSalesOrder(buildUpdateSalesOrderPayload(payload, false));
              // eslint-disable-next-line no-console
              console.log("[SO] updateSalesOrder submit response:", res);
              toast.success('Sales Order updated and submitted');
            } else {
              res = await adminServices.createSalesOrder(buildSalesOrderPayload(payload, false));
              // eslint-disable-next-line no-console
              console.log("[SO] createSalesOrder submit response:", res);
              toast.success('Sales Order submitted');
            }
            await fetchSalesOrders();
          } catch (error) {
            console.error('Error submitting Sales Order:', error);
            toast.error(error.response?.data?.message || 'Failed to submit Sales Order');
            throw error;
          } finally {
            setSaving(false);
          }
        }}
      />

    </div>
  );
};

export default ProcessOrderView;
